import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Home, HeartHandshake, BookOpen, User } from 'lucide-react'
import { motion, useAnimationControls } from 'framer-motion'
import { DonationPopup } from '@/components/DonationPopup'
import { useProfile } from '@/hooks/useProfile'
import { useSwipeBack } from '@/hooks/useSwipeBack'
import { useKeyboardHeight } from '@/hooks/useKeyboardHeight'

const NAV_ITEMS = [
  { icon: Home,      label: 'Accueil',  path: '/app' },
  { icon: HeartHandshake, label: 'Prières',  path: '/app/prayer' },
  { icon: BookOpen,  label: 'Journal',  path: '/app/journal' },
  { icon: User,      label: 'Profil',   path: '/app/profile' },
] as const

// Pages that show the bottom nav
const SHOW_NAV_PATHS = ['/app', '/app/prayer', '/app/journal', '/app/profile', '/app/goals', '/app/library', '/app/notes', '/app/groups']

// Note : la liste des 4 tabs principaux (sans swipe back) est gérée dans
// useSwipeBack lui-même — plus besoin de la dupliquer ici.

const DONATION_STREAKS: Array<7 | 30> = [7, 30]

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { profile } = useProfile()

  const [donationDays, setDonationDays] = useState<7 | 30 | null>(null)

  // ── Edge stretch : compresse légèrement la nav vers le côté tapé
  // (effet "squish" Instagram quand on tap la 1re ou la dernière icône) ─────────
  const navControls = useAnimationControls()
  const edgeOriginRef = useRef<'left center' | 'right center'>('left center')
  const [edgeTapCount, setEdgeTapCount] = useState(0)

  useEffect(() => {
    if (edgeTapCount === 0) return
    navControls.start({
      scaleX: [1, 0.96, 1.01, 1],
      transition: { duration: 0.42, ease: [0.34, 1.56, 0.64, 1] },
    })
  }, [edgeTapCount, navControls])

  // Check if we should show donation popup
  useEffect(() => {
    const streak = profile?.streak_count ?? 0
    for (const days of DONATION_STREAKS) {
      const key = `donation_popup_${days}_shown`
      if (streak >= days && !localStorage.getItem(key)) {
        localStorage.setItem(key, '1')
        setDonationDays(days)
        break
      }
    }
  }, [profile?.streak_count])

  // Show bottom nav only on main routes
  const showNav = SHOW_NAV_PATHS.some(p =>
    p === '/app' ? location.pathname === '/app' : location.pathname.startsWith(p)
  )

  const isActive = (path: string) => {
    if (path === '/app') return location.pathname === '/app'
    return location.pathname.startsWith(path)
  }

  // ── Swipe back natif iOS (détection seule, navigate(-1), 0 animation) ──────
  // Le hook gère lui-même l'exclusion des 4 tabs principaux.
  useSwipeBack()

  // ── Keyboard : masquer la tab bar quand le clavier est ouvert ──────────────
  const { isKeyboardOpen } = useKeyboardHeight()

  // ── Scroll-to-top INSTANTANÉ à chaque changement de route ─────────────────
  // Cible window + #root (conteneur principal scrollable) + <main>.
  // behavior: 'instant' évite toute animation de scroll lent entre les pages.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior }) }
    catch { window.scrollTo(0, 0) }
    const rootEl = document.getElementById('root')
    if (rootEl) rootEl.scrollTop = 0
    const mainEl = document.querySelector('main')
    if (mainEl) {
      mainEl.scrollTop = 0
      // Reset all scrollable containers within the page
      mainEl.querySelectorAll('[style*="overflow"], .overflow-y-auto, .overflow-auto').forEach(el => {
        (el as HTMLElement).scrollTop = 0
      })
    }
  }, [location.pathname])

  return (
    <div
      className="flex flex-col"
      style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top)', backgroundColor: 'var(--selah-bg)' }}
    >
      {/* Donation popup — only rendered when streak milestone reached */}
      {donationDays !== null && (
        <DonationPopup
          days={donationDays}
          onClose={() => setDonationDays(null)}
        />
      )}

      {/* Page content — navigation instantanée, pas d'animation ni de transform */}
      <main
        className="flex-1"
        style={{
          // Floating navbar (58px) + 12px top buffer + safe-area + 12px bottom margin
          paddingBottom: (showNav && !isKeyboardOpen)
            ? 'calc(70px + env(safe-area-inset-bottom) + 12px)'
            : 0,
          backgroundColor: 'var(--selah-bg)',
          overflowX: 'hidden',
        }}
      >
        <Outlet />
      </main>

      {/* ── Floating navbar — style Instagram (sliding pill + edge squish) ──── */}
      {showNav && (
        <motion.nav
          animate={navControls}
          className="fixed flex items-center"
          style={{
            left: 12,
            right: 12,
            bottom: 0,
            marginBottom: 'max(12px, env(safe-area-inset-bottom))',
            transform: isKeyboardOpen ? 'translateY(calc(100% + 24px))' : 'translateZ(0)',
            transformOrigin: edgeOriginRef.current,
            height: 58,
            borderRadius: 30,
            backgroundColor: 'var(--selah-navbar-bg)',
            backdropFilter: 'blur(30px) saturate(180%)',
            WebkitBackdropFilter: 'blur(30px) saturate(180%)',
            border: '1px solid var(--selah-navbar-border)',
            boxShadow: 'var(--selah-navbar-shadow)',
            padding: '8px 12px',
            zIndex: 50,
            willChange: 'transform, opacity',
            opacity: isKeyboardOpen ? 0 : 1,
            transition: 'transform 200ms ease-out, opacity 200ms ease-out',
            pointerEvents: isKeyboardOpen ? 'none' : 'auto',
          }}
        >
          {NAV_ITEMS.map(({ icon: Icon, label, path }, i) => {
            const active = isActive(path)
            const isFirst = i === 0
            const isLast = i === NAV_ITEMS.length - 1
            return (
              <button
                key={path}
                onClick={() => {
                  // Edge squish : si on tap la 1re ou la dernière icône,
                  // la barre se compresse légèrement vers ce côté.
                  if (isFirst || isLast) {
                    edgeOriginRef.current = isFirst ? 'left center' : 'right center'
                    setEdgeTapCount(c => c + 1)
                  }
                  if (active && path === '/app') {
                    // Déjà sur l'accueil → remonter vers le haut en douceur
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  } else {
                    navigate(path)
                  }
                }}
                className="flex-1 h-full flex items-center justify-center relative"
                style={{
                  color: active
                    ? 'var(--selah-navbar-icon-active)'
                    : 'var(--selah-navbar-icon-inactive)',
                  transition: 'color 0.2s ease',
                  background: 'transparent',
                  border: 'none',
                }}
                aria-label={label}
              >
                {/* Sliding pill — un seul élément partagé qui se faufile
                    entre les boutons via layoutId (spring framer-motion) */}
                {active && (
                  <motion.span
                    layoutId="navbar-active-pill"
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 6,
                      right: 6,
                      borderRadius: 14,
                      backgroundColor: 'var(--selah-navbar-pill)',
                      zIndex: 0,
                    }}
                    transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.8 }}
                  />
                )}
                <Icon
                  size={28}
                  strokeWidth={active ? 2.2 : 1.8}
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    transition: 'stroke-width 0.2s ease',
                  }}
                />
              </button>
            )
          })}
        </motion.nav>
      )}
    </div>
  )
}
